import React, { useMemo, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { PROGRAMS, MapleProgram } from '../../utils/programs';
import { FileSystemNavigator } from '../../utils/filesystem';
import { globalNavigator } from '../WindowContent';

type RunProps = {
  onOpenWindow?: (appId: string, title: string, filePath?: string) => void;
  navigator?: FileSystemNavigator;
};

const Run: React.FC<RunProps> = ({ onOpenWindow, navigator }) => {
  const [programText, setProgramText] = useState('');
  const [pathText, setPathText] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(0);
  const nav = navigator || globalNavigator;

  const suggestions = useMemo(() => {
    const q = programText.trim().toLowerCase();
    if (!q) return PROGRAMS.slice(0, 10);
    return PROGRAMS.filter(p =>
      p.label.toLowerCase().includes(q) || p.id.toLowerCase().includes(q)
    ).slice(0, 10);
  }, [programText]);

  const chosenProgram = useMemo<MapleProgram | null>(() => {
    const q = programText.trim().toLowerCase();
    if (!q) return null;
    return PROGRAMS.find(p => p.label.toLowerCase() === q || p.id.toLowerCase() === q) || null;
  }, [programText]);

  const run = () => {
    const program = chosenProgram || suggestions[0];
    if (!program || !onOpenWindow) return;

    if (pathText.trim()) {
      const path = pathText.trim();
      // If it looks like a folder/file path, pass along — file apps will interpret it
      onOpenWindow(program.id, program.label, path);
    } else {
      onOpenWindow(program.id, program.label);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex((i) => Math.min(i + 1, Math.max(0, suggestions.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = suggestions[focusedIndex] || suggestions[0];
      if (pick) {
        setProgramText(pick.label);
        setTimeout(run, 0);
      } else {
        run();
      }
    }
  };

  return (
    <div className="h-full w-full p-4">
      <Card className="h-full flex flex-col">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Run</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col gap-3">
          <div className="space-y-2">
            <label className="text-sm">Type the name of a program</label>
            <Input
              placeholder="e.g., Notepad, File Explorer, Terminal"
              value={programText}
              onChange={(e) => {
                setProgramText(e.target.value);
                setFocusedIndex(0);
              }}
              onKeyDown={onKeyDown}
            />
            <ScrollArea className="max-h-40 border rounded">
              <div className="p-1">
                {suggestions.map((s, idx) => {
                  const Icon = s.icon;
                  const active = idx === focusedIndex;
                  return (
                    <Button
                      key={s.id}
                      variant={active ? 'default' : 'ghost'}
                      size="sm"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        setProgramText(s.label);
                        setFocusedIndex(idx);
                      }}
                    >
                      <Icon className="h-4 w-4" />
                      {s.label}
                      <span className="text-[11px] text-muted-foreground ml-auto">{s.id}</span>
                    </Button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          <div className="space-y-2">
            <label className="text-sm">Optional path</label>
            <Input
              placeholder="C:\\User\\YourName\\Documents\\Notes.txt (optional)"
              value={pathText}
              onChange={(e) => setPathText(e.target.value)}
            />
          </div>

          <div className="mt-2 flex gap-2">
            <Button onClick={run}>Run</Button>
            <Button variant="outline" onClick={() => { setProgramText(''); setPathText(''); }}>Cancel</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Run;